import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { browserHistory } from 'react-router';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { SetMapLayer, SetWaterLevel, SetControlsEnabled } from '../../actions';
import Header from '../Header';
import ControlPanel from '../ControlPanel';
import Map from '../Map';
import Summary from '../Summary';
import WaterSlider from '../WaterSlider';
import ExploreFurther from '../ExploreFurther';
import HeaderPlaceName from '../HeaderPlaceName';
import ImpactsData from '../ImpactsData';
import Impacts from '../Impacts';
import ForecastData from '../ForecastData';
import Forecast from '../Forecast';
import PicturingChoices from '../PicturingChoices';
import NuisanceFloodingData from '../NuisanceFloodingData';
import NuisanceFlooding from '../NuisanceFlooding';
import ReducingYourRiskData from '../ReducingYourRiskData';
import ReducingYourRisk from '../ReducingYourRisk';
import RegionEAEData from "../RegionEAEData";
import RegionEAE from "../RegionEAE";
import ToggleText from '../ToggleText';
import ZillowData from '../ZillowData';
import Zillow from '../Zillow';
import * as d3 from 'd3';

import styles from './PlacePage.css';
import withStyles from '../../decorators/withStyles';
import { gaEvent, isUS, simplePlaceName, xlt } from "../../utils/utils";


class PlacePage extends Component {

  static propTypes = {
    params: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      categories: {},
      related: [],
      scrollableHeader: "fixed",
      componentKey: 0,
      refs: []
    };

    // Cross-browser scroll-state compatibility, per MDN:
    // https://developer.mozilla.org/en-US/docs/Web/API/Window/scrollY
    const supportPageOffset = window.pageXOffset !== undefined;
    const isCSS1Compat = ((document.compatMode || "") === "CSS1Compat");
    this.scrollX = () => supportPageOffset ? window.pageXOffset
                          : isCSS1Compat ? document.documentElement.scrollLeft : document.body.scrollLeft;
    this.scrollY = () => supportPageOffset ? window.pageYOffset
                          : isCSS1Compat ? document.documentElement.scrollTop : document.body.scrollTop;
  }

  componentDidMount() {
    window.addEventListener('scroll', this.handleScroll);
    Transifex.live.onBeforeTranslatePage(() => {
      this.setState({componentKey: this.state.componentKey === 0 ? 1 : 0});
    });
  }

  setTxContent = el => {
    // this.state.refs.push(el);
    d3.select(el).attr("tx-content", "block");
    // d3.select(el).attr("tx-dynamic", "disabled");
  };

  hasWaterSlider = () => {
    const { forecastName } = this.props;

    const enableSlider = (el) => {
      const scroll = el.getBoundingClientRect().top;
      return (scroll < 400 && ((scroll + el.clientHeight) > 400));
    };

    const includedClasses = [
      'Forecast',
      'Impacts',
      'Summary',
    ];

    let enabled = false;

    for (let className of includedClasses) {
      const element = d3.select(`section > .${className}`);
      const relevantForecast = forecastName === 'Multi-year' || forecastName === 'Single-year';
      const isRelevant = className !== 'Forecast' || relevantForecast;
      if (element && element.node() && enableSlider(element.node()) && isRelevant) {
        enabled = true;
        break;
      }
    }

    return enabled;
  };

  handleScroll = e => {
    const { dispatch } = this.props;
    let scrollChange;
    try {
      const summary = d3.select(".Summary");
      const clientHeight = summary.property('clientHeight');
      scrollChange = clientHeight + 35;
    } catch (_) {
      // scroll is sometimes called after .Summary is unmounted during a page transition,
      // resulting in a null dereferencing error. In this case there's nothing to handle here.
      return;
    }
    if (this.scrollY() >= scrollChange && this.state.scrollableHeader !== "scrollable") {
      this.setState({scrollableHeader: "scrollable"});
    } else if (this.scrollY() < scrollChange && this.state.scrollableHeader === "scrollable") {
      this.setState({scrollableHeader: "fixed"});
    }

    const enabled = this.hasWaterSlider();

    dispatch(SetControlsEnabled(enabled));
  };

  render() {
    const { controlType, place, placeContainer, dispatch, placeName, waterLevel, forecastCtrl,
            forecastName, forecastType, mapPosition, mapLayer, mapCaption, aslrStation, stations } = this.props;
    const placeType = place.type;
    const placeID = place.id;
    if (!placeID || !placeType || placeName === "") {
      return null;
    }

    const picturingChoicesURL = placeID => {
      var url;
      var name;
      if (placeID === "guyana" || placeID.endsWith(".gy")) {
        url = "/static/img/georgetown_stgeorge_4C_highres.jpg";
        name = "Georgetown, Guyana";
      } else if (placeID === "jamaica" || placeID.endsWith(".jm")) {
        url = "/static/img/kingston_4C_highres.jpg";
        name = "Kingston, Jamaica";
      } else if (placeID === "bahamas" || placeID.endsWith(".bs")) {
        url = "/static/img/nassau_cathedral_4C_highres.jpg";
        name = "Nassau, The Bahamas";
      } else {
        return null;
      }
      return {url, name};
    };

    var bandCounter = -1;
    var bandColor = () => {bandCounter += 1; return bandCounter % 2 === 0 ? "light" : "dark"};

    return (
      <div className="container" key={this.state.componentKey.toString()}>
        <Header
          mobileMenuOpen={this.props.mobileMenuOpen}
          modalVisible={this.props.modalVisible}
          searchable={this.props.routes[this.props.routes.length - 1].searchable}
        />
        <ControlPanel className={this.state.scrollableHeader}>
          <WaterSlider
            waterLevel={waterLevel}
            onLevelChange={(unit, level) => { gaEvent('configure', 'click', 'water-slider-big');
                                              dispatch(SetWaterLevel({level, unit})); }}
          />
        </ControlPanel>
        <main>
          <div className={"band highlight " + this.state.scrollableHeader}>
            <HeaderPlaceName
              placeName={placeName}
              placeID={placeID}
              placeType={placeType}
            />
          </div>
          <div className="spacer"></div>
          <div className={"band " + bandColor()} >
            <section>
              <Summary
                waterLevel={waterLevel}
                placeType={placeType}
                placeID={placeID}
                placeName={placeName}
                modalVisible={this.props.modalVisible} >
                <Map waterLevel={waterLevel} position={mapPosition} mapLayer={mapLayer}>
                    { (controlType === "water_level") ?
                        (mapLayer === "sovi" ?
                            <p className="map-explain" ref={this.setTxContent} key={Math.random().toString()}>{mapCaption} {mapCaption === "" ? (<span><var>{xlt(simplePlaceName(placeName))}</var> area land</span>) : "Land"} below <var>{waterLevel.level}</var> <var>{waterLevel.unit}</var> is
                            colored yellow through red to denote populations with low through high social vulnerability.
                            Social vulnerability (e.g. from low income) can compound coastal risk. Maroon lines are levees.
                             See full-feature map for legends and details. <ToggleText><span onClick={() => {this.props.dispatch(SetMapLayer("property")); gaEvent('map-layer', 'click', 'property');}}>Switch to property value map layer</span></ToggleText>
                            </p>
                            : mapLayer === "property" ?
                            <p className="map-explain" ref={this.setTxContent} key={Math.random().toString()}>{mapCaption} {mapCaption === "" ? (<span><var>{xlt(simplePlaceName(placeName))}</var> area land</span>) : "Land"} below <var>{waterLevel.level}</var> <var>{waterLevel.unit}</var> is
                            colored yellow through red to denote zones with low through
                            high property value per acre. Blue shading indicates missing data or $0. Any stippling indicates sub-<var>{waterLevel.level}</var><var>{waterLevel.unit}</var> areas
                            that levees (maroon lines) or ridges may protect.
                             See full-feature map for legends and details. <ToggleText><span onClick={() => {this.props.dispatch(SetMapLayer("sovi")); gaEvent('map-layer', 'click', 'sovi');}}>Switch to social vulnerability map layer</span></ToggleText>
                            </p>
                            :
                            <p className="map-explain" ref={this.setTxContent} key={Math.random().toString()}><var>{xlt(simplePlaceName(placeName))}</var> area land
                      below <var>{waterLevel.level} </var>{waterLevel.unit}.
                            </p>)
                        : (controlType === "scenario") ?
                        <p className="map-explain" ref={this.setTxContent} key={Math.random().toString()}><var>{xlt(simplePlaceName(placeName))}</var> area land inundated
                            under {forecastCtrl.scenario} by {forecastCtrl.year} ({forecastCtrl.sensitivity} percentile estimate)</p>
                        : null}
                </Map>
              </Summary>
            </section>
          </div>
          {stations.length > 0 ?
              <div className={"band " + bandColor()} >
                <section>
                  <ForecastData
                      placeType={placeType}
                      placeID={placeID}
                      station={stations[this.props.stationNum]}
                      forecastName={forecastName}
                      forecastType={forecastType}
                      waterLevel={waterLevel}
                      metadata={this.state.forecastMeta}
                  >
                    <Forecast onScroll={this.handleHeaderScroll} />
                  </ForecastData>
                </section>
              </div>
              : null}
          {aslrStation ?
            <div className={"band " + bandColor()}>
              <section>
                <NuisanceFloodingData
                  placeType={placeType}
                  placeID={placeID}
                  station={aslrStation}
                  forecastName={forecastName}
                  forecastType={forecastType}
                  waterLevel={waterLevel}
                >
                  <NuisanceFlooding />
                </NuisanceFloodingData>
              </section>
            </div>
            : null}
          {
          <div className={"band " + bandColor()}>
            <section>
              <ImpactsData
                  placeType={placeType}
                  placeID={placeID}
                  waterLevel={waterLevel}
                  forecastCtrl={forecastCtrl}
              >
                <Impacts />
              </ImpactsData>
            </section>
          </div>
          }
          {isUS(placeID) ?
            <div className={"band " + bandColor()}>
              <section>
                <ZillowData
                    placeType={placeType}
                    placeID={placeID}
                >
                  <Zillow />
                </ZillowData>
              </section>
            </div>
            : null}
          {this.props.eae.length > 0 ?
          <div className={"band " + bandColor()}>
            <section>
              <RegionEAEData
                  placeType={placeType}
                  placeID={placeID}
              >
                <RegionEAE />
              </RegionEAEData>
            </section>
            </div>
          : null}
          {picturingChoicesURL(placeID) ?
              <div className={"band " + bandColor()}>
                <PicturingChoices
                    location={picturingChoicesURL(placeID)}
                    modalVisible={this.props.modalVisible}
                />
              </div>
              : null}
          {isUS(placeID) ?
              <div className={"band " + bandColor()}>
                <ReducingYourRiskData
                    placeType={placeType}
                    placeID={placeID}
                    placeName={placeName}
                    placeContainer={placeContainer}
                >
                  <ReducingYourRisk />
                </ReducingYourRiskData>
              </div>
              : null}
          {isUS(placeID) ?
              <div className={"band " + bandColor()}>
                <ExploreFurther
                    placeType={placeType}
                    placeID={placeID}
                    placeName={placeName}
                    relatedPlaces={this.state.related}
                    placeContainer={placeContainer}
                    query={this.props.q}
                />
              </div>
              : null}
        </main>
      </div>
    )
  }
}

export default compose(
  withStyles(styles),
  connect((state) => state),
)(PlacePage);
